import { useState, useEffect, useCallback } from 'react';
import { getUnseenReviews, markReviewsSeen, getReviewHistory } from '../api/students';
import { reportUserFacingError } from '../../../utils/errorReporting';

/**
 * Manages review modal state for both unseen (auto-popup) and history (manual) modals.
 * @param {string} studentId
 * @param {boolean} isSignedIn
 * @param {Object} user
 * @param {boolean} isTeacherView
 * @param {function} onStreakRefresh - Callback to refresh streak data after marking seen
 * @returns {{
 *   showReviewModal: boolean,
 *   unseenReviews: { reviews: Array, summary: Object | null },
 *   showHistoryModal: boolean,
 *   reviewHistory: { reviews: Array, summary: Object | null },
 *   handleCloseReviewModal: () => Promise<void>,
 *   handleNotificationClick: () => Promise<void>,
 *   handleCloseHistoryModal: () => void,
 *   unseenCount: number
 * }}
 */
export function useReviewModals(studentId, isSignedIn, user, isTeacherView, onStreakRefresh) {
  // Unseen reviews modal state (auto-popup)
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [unseenReviews, setUnseenReviews] = useState({ reviews: [], summary: null });

  // History modal state (manual open via bell icon)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [reviewHistory, setReviewHistory] = useState({ reviews: [], summary: null });

  // Load unseen reviews on mount
  useEffect(() => {
    let cancelled = false;

    const loadUnseenReviews = async () => {
      if (!studentId || !isSignedIn || !user) return;

      try {
        const data = await getUnseenReviews(studentId);
        if (cancelled) return;

        // A quiz-only day (no teacher reviews) opens the mail too.
        if (data?.reviews?.length > 0 || data?.quizzes?.length > 0) {
          setUnseenReviews(data);
          setShowReviewModal(true);
        }
      } catch (error) {
        if (cancelled) return;
        reportUserFacingError(error, { context: 'Load unseen reviews' });
      }
    };

    loadUnseenReviews();

    return () => {
      cancelled = true;
    };
  }, [studentId, isSignedIn, user]);

  // Handle closing the review modal - mark reviews as seen and refresh streak data
  const handleCloseReviewModal = useCallback(async () => {
    setShowReviewModal(false);

    // Only mark reviews as seen if this is the actual student (not teacher preview)
    if (studentId && !isTeacherView) {
      try {
        await markReviewsSeen(studentId);
        // Clear unseen count since they've been marked as seen
        setUnseenReviews({ reviews: [], summary: null });
        // Refresh streak data to get updated coin balance
        if (onStreakRefresh) {
          await onStreakRefresh();
        }
      } catch (error) {
        reportUserFacingError(error, { context: 'Mark reviews as seen' });
      }
    }
  }, [studentId, isTeacherView, onStreakRefresh]);

  // Handle notification bell click - fetch history and open modal
  const handleNotificationClick = useCallback(async () => {
    if (!studentId) return;

    try {
      const data = await getReviewHistory(studentId);
      setReviewHistory(data || { reviews: [], summary: null });
      setShowHistoryModal(true);
    } catch (error) {
      reportUserFacingError(error, { context: 'Load review history' });
    }
  }, [studentId]);

  // Handle closing the history modal (no marking as seen needed)
  const handleCloseHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
  }, []);

  return {
    showReviewModal,
    unseenReviews,
    showHistoryModal,
    reviewHistory,
    handleCloseReviewModal,
    handleNotificationClick,
    handleCloseHistoryModal,
    unseenCount: unseenReviews.reviews?.length || 0,
  };
}

export default useReviewModals;
